// src/pages/reprint/MediaList.jsx
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/ko'; // 한국어 로케일 추가
import '@assets/css/reprint/style_reprint.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_calendar.css';
import Calendar from '@components/common/Calendar.jsx';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';
import printApi from '@services/printApi.jsx';

// moment 한국어 로케일 설정
moment.locale('ko');

function MediaList() {
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterType, setFilterType] = useState('all');
    // 선택된 미디어 정보
    const [selectedMedia, setSelectedMedia] = useState(null); // 선택된 미디어 정보
    const [selectedMode, setSelectedMode] = useState('사진'); // 선택된 모드
    const [showAlertModal, setShowAlertModal] = useState(false); // 알림 모달 상태
    const [alertMessage, setAlertMessage] = useState(''); // 알림 메시지
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
    const [activeInput, setActiveInput] = useState(null);

    // Firebase 미디어 데이터 상태
    const [mediaData, setMediaData] = useState({
        content: [],
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    });
    
    const [currentPage, setCurrentPage] = useState(0);

    // 컴포넌트 마운트 시 초기 데이터 로드
    useEffect(() => {
        loadMediaList();
    }, [currentPage]);

    /**
     * Firebase 미디어 리스트 조회
     */
    const loadMediaList = async () => {
        setIsLoading(true);
        try {
            const params = {
                startDatetime: startDate ? convertDateFormat(startDate, false) : null, // 시작일 00:00
                endDatetime: endDate ? convertDateFormat(endDate, true) : null, // 종료일 23:59
                branchNo: filterType !== 'all' ? filterType : null,
                page: currentPage,
                size: 10,
                sortDirection: 'desc'
            };

            const response = await printApi.getMediaList(params);
            setMediaData(response);
            
            // 선택 상태 초기화
            setSelectedMedia(null);
            
        } catch (error) {
            console.error('미디어 리스트 조회 실패:', error);
            alert('미디어 리스트를 불러오는데 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * 날짜 형식 변환 (YYYYMMDD -> YYYYMMDDHHMM)
     * @param {string} dateStr - YYYYMMDD 형식
     * @param {boolean} isEndDate - 종료일인지 여부 (true면 23:59, false면 00:00)
     */
    const convertDateFormat = (dateStr, isEndDate = false) => {
        if (!dateStr || dateStr.length !== 8) return null;
        
        try {
            const momentObj = moment(dateStr, 'YYYYMMDD');
            if (!momentObj.isValid()) return null;
            
            // 시작일은 00:00, 종료일은 23:59로 설정
            const timeStr = isEndDate ? '2359' : '0000';
            return momentObj.format('YYYYMMDD') + timeStr;
        } catch (error) {
            console.error('날짜 변환 오류:', error);
            return dateStr + (isEndDate ? '2359' : '0000'); // 폴백
        }
    };

    /**
     * 미디어 타입 판별 (goodsId 기반)
     */
    const getMediaType = (item) => {
        const goodsId = item.goodsId;
        if (goodsId === 'p' || goodsId === 'pv') {
            return '사진';
        }
        return '동영상';
    };

    /**
     * 사진 만료 시간 체크
     * @param {string} expiredDatetime - YYYYMMDDHHMM 또는 YYYYMMDDHHMMSS 형식
     * @returns {boolean} 만료되지 않았으면 true
     */
    const isPhotoValid = (expiredDatetime) => {
        if (!expiredDatetime) return false;
        
        try {
            // 문자열에서 직접 날짜 부분 추출
            const year = parseInt(expiredDatetime.substring(0, 4));
            const month = parseInt(expiredDatetime.substring(4, 6)) - 1; // month는 0-based
            const day = parseInt(expiredDatetime.substring(6, 8));
            const hour = parseInt(expiredDatetime.substring(8, 10));
            const minute = parseInt(expiredDatetime.substring(10, 12));
            const second = expiredDatetime.length >= 14 ? parseInt(expiredDatetime.substring(12, 14)) : 0;
            
            // moment 객체 생성
            const expiredMoment = moment([year, month, day, hour, minute, second]);
            
            if (!expiredMoment.isValid()) {
                return false;
            }
            
            const now = moment();
            const result = now.isBefore(expiredMoment);
            
            return result;
        } catch (error) {
            console.error('만료시간 체크 오류:', error);
            return false;
        }
    };

    /**
     * 미리보기 이미지 URL 가져오기
     */
    const getPreviewImageUrl = (item) => {
        const goodsId = item.goodsId;

        // goodsId가 p 또는 pv인 경우
        if (goodsId === 'p' || goodsId === 'pv') {
            // photoStorageUrl이 있고 만료시간이 지나지 않았을 경우
            if (item.photoStorageUrl && isPhotoValid(item.photoExpiredDatetime)) {
                // 개발 환경에서만 프록시 서버를 통해 이미지 로드
                if (import.meta.env.VITE_ENV === 'dev') {
                    // pub-image.postme.co.kr을 image.postme.co.kr로 변경하고 프록시 서버 사용
                    return `https://file-worker.postme.co.kr/download?path=${item.photoStorageUrl}`;
                }
                return item.photoStorageUrl;
            }
        }

        // 조건에 맞지 않는 경우 null 반환 (기본 이미지 사용)
        return null;
    };

    /**
     * 업로드 일시 포맷팅 (YYYY/MM/DD HH:mm:ss)
     */
    const formatUploadDatetime = (item) => {
        const datetime = item.photoUploadDatetime || item.videoUploadDatetime;
        if (!datetime) return '';
        
        try {
            // YYYYMMDDHHMM 형식을 moment로 파싱
            let momentObj;
            
            if (datetime.length >= 14) {
                // YYYYMMDDHHMMSS 형식
                momentObj = moment(datetime, 'YYYYMMDDHHMMSS');
            } else if (datetime.length >= 12) {
                // YYYYMMDDHHMM 형식 (초는 00으로 가정)
                momentObj = moment(datetime + '00', 'YYYYMMDDHHMMSS');
            } else {
                // 기타 형식은 그대로 반환
                return datetime;
            }
            
            // 유효한 날짜인지 확인
            if (!momentObj.isValid()) {
                return datetime;
            }
            
            // YYYY/MM/DD HH:mm:ss 형식으로 포맷팅 (소문자 ss 사용)
            return momentObj.format('YYYY/MM/DD HH:mm:ss');
            
        } catch (error) {
            console.error('날짜 포맷팅 오류:', error);
            return datetime;
        }
    };



    // 날짜 선택 핸들러
    const handleDateSelect = (startDate, endDate) => {
        const formatDate = (date) => {
            if (!date) return ''; // null이나 undefined 체크 추가
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}${month}${day}`;
        };

        const formattedDate = formatDate(startDate);
        
        // 활성화된 입력 필드에 따라 다르게 처리
        if (activeInput === 'start') {
            setStartDate(formattedDate);
        } else if (activeInput === 'end') {
            setEndDate(formattedDate);
        }
        
        setShowCalendar(false);
        setActiveInput(null);
    };

    // 입력 필드 클릭 핸들러
    const handleInputClick = (inputType, event) => {
        const input = event.target;
        const rect = input.getBoundingClientRect();

        setCalendarPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left
        });

        setActiveInput(inputType);
        setShowCalendar(true);
    };

    /**
     * 행 클릭 핸들러 - 미디어 선택
     */
    const handleRowClick = (item) => {
        setSelectedMedia(item);
        setSelectedMode(getMediaType(item));
    };

    /**
     * 사진이 유효한지 체크 (인쇄 가능 여부)
     */
    const canPrint = (item) => {
        if (!item) return false;
        const goodsId = item.goodsId;
        
        // goodsId가 p 또는 pv인 경우
        if (goodsId === 'p' || goodsId === 'pv') {
            // photoStorageUrl이 있고 만료시간이 지나지 않았을 경우
            return item.photoStorageUrl && isPhotoValid(item.photoExpiredDatetime);
        }
        
        return false;
    };

    /**
     * QR 출력 가능 여부 체크
     */
    const canPrintQr = (item) => {
        if (!item) return false;
        
        // photoStorageQrUrl이 있어야 함
        if (!item.photoStorageQrUrl) return false;
        
        // 사진 또는 동영상 만료시간 중 하나라도 유효해야 함
        const photoValid = item.photoExpiredDatetime && isPhotoValid(item.photoExpiredDatetime);
        const videoValid = item.videoExpiredDatetime && isPhotoValid(item.videoExpiredDatetime);
        
        return photoValid || videoValid;
    };

    /**
     * 사진 출력 버튼 핸들러
     */
    const handlePhotoPrint = async () => {
        if (!selectedMedia) {
            setAlertMessage('사진을 선택해주세요.');
            setShowAlertModal(true);
            return;
        }
        
        if (!canPrint(selectedMedia)) {
            setAlertMessage('사진이 없거나 만료되어 인쇄할 수 없습니다.');
            setShowAlertModal(true);
            return;
        }
        
        try {
            setIsLoading(true);
            
            // 직접 프린트 요청 생성 (관리자용)
            const printRequest = {
                requestType: 'PHOTO',
                branchNo: selectedMedia.branchNo || 'A001',
                robotNo: selectedMedia.robotNo || 'PRA001',
                uuid: selectedMedia.uuid,
                year: selectedMedia.year,
                month: selectedMedia.month,
                day: selectedMedia.day,
                filePath: selectedMedia.photoStorageUrl,
                isCut: false, // 기본값
                amount: 1 // 기본값
            };
            
            const response = await printApi.createPrintRequest(printRequest);
            setAlertMessage(`사진 인쇄 요청이 완료되었습니다. (요청 ID: ${response.id})`);
            setShowAlertModal(true);
            
        } catch (error) {
            console.error('사진 인쇄 요청 실패:', error);
            const errorMessage = error.response?.data?.message || '사진 인쇄 요청 중 오류가 발생했습니다.';
            setAlertMessage(errorMessage);
            setShowAlertModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * QR 출력 버튼 핸들러
     */
    const handleQrPrint = async () => {
        if (!selectedMedia) {
            setAlertMessage('미디어를 선택해주세요.');
            setShowAlertModal(true);
            return;
        }
        
        if (!canPrintQr(selectedMedia)) {
            setAlertMessage('QR 코드가 없거나 만료되어 QR을 인쇄할 수 없습니다.');
            setShowAlertModal(true);
            return;
        }
        
        try {
            setIsLoading(true);
            
            // 직접 프린트 요청 생성 (관리자용)
            const printRequest = {
                requestType: 'QR',
                branchNo: selectedMedia.branchNo || 'DEFAULT_BRANCH',
                robotNo: selectedMedia.robotNo || 'DEFAULT_ROBOT',
                uuid: selectedMedia.uuid,
                year: selectedMedia.year,
                month: selectedMedia.month,
                day: selectedMedia.day
            };
            
            const response = await printApi.createPrintRequest(printRequest);
            setAlertMessage(`QR 인쇄 요청이 완료되었습니다. (요청 ID: ${response.id})`);
            setShowAlertModal(true);
            
        } catch (error) {
            console.error('QR 인쇄 요청 실패:', error);
            const errorMessage = error.response?.data?.message || 'QR 인쇄 요청 중 오류가 발생했습니다.';
            setAlertMessage(errorMessage);
            setShowAlertModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    // 검색 버튼 핸들러
    const handleSearch = () => {
        setCurrentPage(0);
        loadMediaList();
    };

    // 페이지 변경 핸들러
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < mediaData.totalPages) {
            setCurrentPage(newPage);
        }
    };



    // 로딩 상태 변경시 페이지 로더 표시 처리
    useEffect(() => {
        const pageLoader = document.querySelector('.page-loader');
        if (pageLoader) {
            if (isLoading) {
                pageLoader.classList.add('active');
            } else {
                pageLoader.classList.remove('active');
            }
        }
    }, [isLoading]);


    return (
        <>
            {/* 리프린트 콘텐츠 시작 */}
            <div className="page-title-container">
                <h1 className="page-title">미디어리스트</h1>
            </div>

            <div className="reprint-container">
                {/* 좌측 테이블 영역 */}
                <div className="reprint-table-section">
                    {/* 필터 영역 */}
                    <div className="filter-container data-section-filter">
                        <h3 className="section-title">사용기록</h3>
                        <div className="filter-controls-container">
                            <div className="date-filter">
                                <input
                                    type="text"
                                    className={`date-input ${activeInput === 'start' ? 'active' : ''}`}
                                    id="start-date"
                                    placeholder="시작일 (YYYYMMDD)"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    onClick={(e) => handleInputClick('start', e)}
                                    readOnly
                                />
                                <span>~</span>
                                <input
                                    type="text"
                                    className={`date-input ${activeInput === 'end' ? 'active' : ''}`}
                                    id="end-date"
                                    placeholder="종료일 (YYYYMMDD)"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    onClick={(e) => handleInputClick('end', e)}
                                    readOnly
                                />
                            </div>
                            <select
                                className="filter-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">전체</option>
                                <option value="branch">지점</option>
                            </select>
                            <button className="search-btn" onClick={handleSearch}>검색</button>
                        </div>
                    </div>

                    {/* 테이블 */}
                    <div className="common-table-container">
                        <table className="common-table">
                            <thead>
                            <tr>
                                <th>NO</th>
                                <th>브랜치No</th>
                                <th>로봇No</th>
                                <th>일시</th>
                                <th>사용 모드</th>
                                <th>UUID</th>
                            </tr>
                            </thead>
                            <tbody>
                            {mediaData.content.length > 0 ? (
                                mediaData.content.map((item, index) => (
                                    <tr 
                                        key={item.uuid} 
                                        onClick={() => handleRowClick(item)}
                                        className={`table-row ${selectedMedia?.uuid === item.uuid ? 'selected' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{currentPage * 10 + index + 1}</td>
                                        <td>{item.branchNo || ''}</td>
                                        <td>{item.robotNo || ''}</td>
                                        <td>{formatUploadDatetime(item)}</td>
                                        <td>{getMediaType(item)}</td>
                                        <td>{item.uuid.substring(0, 8)}...</td>
                                    </tr>
                                ))
                            ) : (
                                // 데이터가 없을 때 빈 행 표시
                                Array(10).fill().map((_, index) => (
                                    <tr key={`empty-${index}`}>
                                        <td colSpan="6">&nbsp;</td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    <PaginationZeroBased
                        page={currentPage}
                        totalPages={mediaData.totalPages}
                        totalElements={mediaData.totalElements}
                        size={mediaData.size}
                        hasNext={mediaData.hasNext}
                        hasPrevious={mediaData.hasPrevious}
                        onPageChange={handlePageChange}
                    />
                </div>

                {/* 우측 프리뷰 영역 */}
                <div className="reprint-preview-container">
                    <h3 className="section-title">수동처리</h3>
                    <div className="preview-image-container">
                        <span className="preview-image-title">{selectedMode}</span>
                        {selectedMedia ? (
                            selectedMode === '사진' ? (
                                (() => {
                                    const previewUrl = getPreviewImageUrl(selectedMedia);
                                    return previewUrl ? (
                                        <img 
                                            src={previewUrl} 
                                            alt="선택된 사진" 
                                            className="preview-image" 
                                            //onError={(e) => { e.target.src = reprintImage; }}
                                        />
                                    ) : (
                                        <div className="no-image-container">
                                            <p className="no-image-text">사진이 없거나 만료되었습니다</p>
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="video-preview">
                                    <div className="video-box">
                                        <p>동영상 QR 코드</p>
                                        {selectedMedia.videoStorageQrUrl && (
                                            <img 
                                                src={selectedMedia.videoStorageQrUrl} 
                                                alt="QR 코드" 
                                                className="qr-image"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="no-selection-container">
                                <p className="no-selection-text">좌측에서 항목을 선택해주세요</p>
                            </div>
                        )}
                    </div>
                    
                    {/* 프린트 버튼들 */}
                    <div className="print-buttons-container">
                        <button 
                            className="print-button photo-print-btn" 
                            onClick={handlePhotoPrint}
                            disabled={!selectedMedia || !canPrint(selectedMedia)}
                        >
                            사진 출력
                        </button>
                        <button 
                            className="print-button qr-print-btn" 
                            onClick={handleQrPrint}
                            disabled={!selectedMedia || !canPrintQr(selectedMedia)}
                        >
                            QR 출력
                        </button>
                    </div>
                </div>
            </div>



            {/* 캘린더 */}
            {showCalendar && (
                <Calendar
                    onDateSelect={handleDateSelect}
                    onClose={() => {
                        setShowCalendar(false);
                        setActiveInput(null);
                    }}
                    position={calendarPosition}
                />
            )}

            {/* 알림 모달 */}
            {showAlertModal && (
                <div className="modal-overlay active" id="alert-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">알림</h3>
                            <button className="modal-close" onClick={() => setShowAlertModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>{alertMessage}</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={() => setShowAlertModal(false)}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default MediaList;